import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PlusCircle, Loader2, Crown, Edit2 } from 'lucide-react';
import { CreateProjectModal } from '../components/CreateProjectModal';

interface Project {
  id: string;
  title: string;
  description: string;
  target_interviews: number;
  interview_count: number;
  owner_id: string;
}

export function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [editorProjects, setEditorProjects] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchProjects();
  }, []);

  async function fetchProjects() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      setCurrentUserId(user.id);

      // Fetch projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('interview_projects')
        .select(`
          id,
          title,
          description,
          target_interviews,
          owner_id,
          interview_count:interviews(count)
        `)
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Fetch editor assignments
      const { data: editorData, error: editorError } = await supabase
        .from('project_editors')
        .select('project_id')
        .eq('user_id', user.id);

      if (editorError) throw editorError;

      // Create a set of project IDs where the user is an editor
      const editorSet = new Set(editorData?.map(e => e.project_id) || []);
      setEditorProjects(editorSet);

      // Transform the count from the subquery into a number
      const projectsWithCount = projectsData?.map(project => ({
        ...project,
        interview_count: project.interview_count?.[0]?.count || 0
      })) || [];

      setProjects(projectsWithCount);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function createProject(projectData: {
    title: string;
    description: string;
    targetInterviews: number;
  }) {
    setIsCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: statusData } = await supabase
        .from('project_statuses')
        .select('id')
        .eq('name', 'Planning')
        .single();

      const { error } = await supabase
        .from('interview_projects')
        .insert({
          title: projectData.title,
          description: projectData.description,
          target_interviews: projectData.targetInterviews,
          status_id: statusData?.id,
          owner_id: user.id
        });

      if (error) throw error;

      setIsModalOpen(false);
      fetchProjects();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsCreating(false);
    }
  }

  const getProgressColor = (current: number, target: number) => {
    if (current === 0) return 'bg-red-500';
    if (current >= target) return 'bg-green-500';
    return 'bg-orange-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-brand-teal">Interview Projects</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your customer interview projects
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange transition-colors"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          New Project
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-brand-teal">
                Project
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-brand-teal">
                Interviews
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-brand-teal">
                Role
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {projects.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-8 text-center text-sm text-gray-500">
                  No projects yet. Create your first project to get started!
                </td>
              </tr>
            ) : (
              projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-brand-orange hover:text-brand-orange-light">
                    <a href={`/projects/${project.id}`}>{project.title}</a>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${getProgressColor(project.interview_count, project.target_interviews)}`} />
                      <span>{project.interview_count}/{project.target_interviews || 0}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {project.owner_id === currentUserId ? (
                      <div className="flex items-center">
                        <Crown className="w-4 h-4 mr-1 text-brand-teal" />
                        <span>Owner</span>
                      </div>
                    ) : editorProjects.has(project.id) ? (
                      <div className="flex items-center">
                        <Edit2 className="w-4 h-4 mr-1 text-brand-teal" />
                        <span>Editor</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-gray-400">
                        <span>Viewer</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={createProject}
        isLoading={isCreating}
      />
    </div>
  );
}